<template>
  <div id="fund-manager">
    <div class="panel">
      <div class="panel-head">
        <h2>Fund Manager</h2>

        <ul class="actions">
          <li>
            <span class="fund-hold" v-if="paused || cancelled" >
              {{ paused ? 'Billing Hold' : 'Client Cancelled' }}
            </span>
            <button
              @click="showFundCreate = true"
              :disabled="paused || cancelled"
              class="action">New Fund</button>
          </li>
        </ul>
      </div>

      <div class="panel-content">
        <div id="month-selector">
          <a @click.prevent="prevMonth" href="#">
            <i class="fa fa-arrow-left"></i> prev month
          </a>
          <h3>{{monthName}} {{year}}</h3>
          <a @click.prevent="nextMonth" href="#">
            next month
            <i class="fa fa-arrow-right"></i>
          </a>
        </div>

        <table class="investment">
          <thead>
            <tr>
              <th>Total Investment</th>
              <th>Management Fee</th>
              <th>Call Tracking Fee</th>
              <th>Click Budget</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                class="highlight-blue"
              >${{ budget.investment_override ? budget.investment_override : budget.investment | USD }}</td>
              <td>${{ budget.management_fee_override ? budget.management_fee_override : budget.management_fee | USD }}</td>
              <td>${{ budget.call_tracking_fee | USD }}</td>
              <td class="highlight-yellow">${{ budget.click_budget | USD }}</td>
            </tr>
          </tbody>
        </table>

        <router-link
          :to="{ name: 'ClientShow', params: { clientId: budget.client_id }}"
          class="client-link"
        >manage client</router-link>

        <div class="table funds" v-if="budget.funds.length > 0">
          <div class="table-row header">
            <div class="table-cell">Fund Type</div>
            <div class="table-cell">Projected</div>
            <div class="table-cell">Actual</div>
            <div class="table-cell">Date Added</div>
            <div class="table-cell">Created By</div>
            <div class="table-cell"></div>
          </div>

          <a
            v-for="(fund, i) in budget.funds"
            :key="i"
            @click.prevent="showFundEdit = true; selectedFund = fund"
            href="#"
            class="table-row fund"
          >
            <div class="table-cell">
              <a href="#" @click.prevent>
                <i class="fa fa-edit"></i>
              </a>
              {{ fund.type }}
              <a v-if="fund.purpose" class="purpose-container" href="#" :title="fund.purpose" @click.prevent>
                <i class="fa fa-info-circle"></i>
                <div class="panel">
                  {{ fund.purpose }}
                </div>
              </a>
            </div>
            <div class="table-cell">
              <span
                v-if="fund.projected !== 0"
                @click.prevent.stop="copyAmount(fund.projected)"
                class="icon"
              >
                <i class="fa fa-copy"></i>
              </span>
              <span
                v-if="(fund.type =='Google Adwords' || fund.type =='Google Ads') && fund.projected < 1"
                class="amount"
              >--</span>
              <span
                v-else
                class="amount"
              >${{ fund.projected ? fund.projected.toLocaleString() : 0 }}</span>
            </div>
            <div class="table-cell">
              <span
                v-if="fund.actual !== 0"
                @click.prevent.stop="copyAmount(fund.actual)"
                class="icon"
              >
                <i class="fa fa-copy"></i>
              </span>
              <span>${{ fund.actual ? fund.actual.toLocaleString() : 0 }}</span>
            </div>
            <div class="table-cell">{{ fund.created_at | moment('MMM D, Y')}}</div>
            <div class="table-cell">{{ fund.created_by }}</div>
            <div class="table-cell">
              <a @click.prevent.stop="destroy(fund)" class="delete" href="#">
                <i class="fa fa-trash"></i>
              </a>
            </div>
          </a>
        </div>

        <div v-else class="no-funds">
          <em>No funds to display</em>
        </div>

        <table class="remaining">
          <thead>
            <tr>
              <th>Click Budget</th>
              <th>Total Allotted Funding</th>
              <th>Current Month Remaining</th>
              <th>Previous Month Over/Under</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="highlight-yellow">${{ budget.click_budget | USD }}</td>
              <td>${{ totalAllotted | USD }}</td>
              <td :class="{ match: checkMatch || (budget.client.zero_balance && remainingBalance == 0) }">${{ remainingBalance | USD }}</td>
              <td
                :class="{ match: prevMonthOverUnder == 0 || checkMatch, over: prevMonthOverUnder > 0, under: prevMonthOverUnder < 0 }"
              >${{ prevMonthOverUnder | USD }}</td>
            </tr>
            <tr>
              <th class="empty"></th>
              <th class="empty"></th>
              <th v-if="budget.client.zero_balance" colspan="2">Remaining Balance</th>
              <th v-else colspan="2">Amount Needed To Balance</th>
            </tr>
            <tr>
              <td class="empty"></td>
              <td class="empty"></td>
              <td v-if="budget.client.zero_balance" colspan="2" :class="{ match: remainingBalance == 0, over: remainingBalance > 0, under: remainingBalance < 0 }">
                ${{ remainingBalance | USD }}
              </td>
              <td v-else colspan="2" :class="{ match: checkMatch }">
                ${{ amountToBalance | USD }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <fund-create
      :show="showFundCreate"
      @update="update()"
      @close="showFundCreate = false"
      :budget="budget"
      :toBalance="amountToBalance"
    ></fund-create>
    <fund-edit
      :show="showFundEdit"
      :fund="selectedFund"
      @update="update()"
      @close="showFundEdit = false"
      :budget="budget"
      :toBalance="amountToBalance"
    ></fund-edit>
  </div>
</template>

<script>
import FundCreate from "./FundCreate";
import FundEdit from "./FundEdit";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "FundManager",
  props: ["budget"],
  components: {
    FundCreate,
    FundEdit
  },
  created() {
    this.getBudgetData();
  },
  data() {
    return {
      showFundCreate: false,
      showFundEdit: false,
      selectedFund: null,
      prevMonthOverUnder: 0,
      months: moment.months()
    };
  },
  watch: {
    month() {
      this.getBudgetData();
    }
  },
  computed: {
    ...mapGetters(["getYear", "getMonth"]),
    year: {
      get() {
        return this.getYear;
      },
      set(value) {
        this.setYear({ year: value });
        this.$emit("update");
      }
    },
    paused() {
      var logHold = false;
      this.budget.client.paid_media_logs.forEach(function(log) {
        if(log.type == "Billing Hold") {
          logHold = true;
        }
      });
      if(this.budget.client.status == 'paused' || logHold) {
        return true;
      }
      return false;
    },
    cancelled() {
      var logCancelled = false;
      this.budget.client.paid_media_logs.forEach(function(log) {
        if(log.type == "Cancellation") {
          logCancelled = true;
        }
      });
      if(this.budget.client.status == 'cancelled' || logCancelled) {
        return true;
      }
      return false;
    },
    monthName() {
      return moment()
        .months(this.month - 1)
        .format("MMMM");
    },
    month: {
      get() {
        return this.getMonth;
      },
      set(value) {
        this.setMonth({ month: value });
        this.$emit("update");
      }
    },
    previousYear() {
      return +this.month == 1 ? +this.year - 1 : +this.year;
    },
    previousMonth() {
      return +this.month == 1 ? 12 : +this.month - 1;
    },
    totalAllotted() {
      return this.budget.funds.reduce((total, fund) => {
        if (fund.type == "Google Adwords" || fund.type == "Google Ads") {
          return total + fund.actual;
        } else {
          return total + fund.projected;
        }
      }, 0);
    },
    remainingBalance() {
      return this.budget.click_budget - this.totalAllotted;
    },
    checkMatch() {
      return this.prevMonthOverUnder + this.remainingBalance == 0;
    },
    checkOver() {
      return (
        this.remainingBalance + this.prevMonthOverUnder > 0 && !this.checkMatch
      );
    },
    checkUnder() {
      return (
        this.remainingBalance + this.prevMonthOverUnder < 0 && !this.checkMatch
      );
    },
    amountToBalance() {
      if(this.budget.client.zero_balance) {
        return this.remainingBalance
      }
      return this.remainingBalance + this.prevMonthOverUnder;
    }
  },
  methods: {
    ...mapActions(["setMonth", "setYear"]),
    prevMonth() {
      if (this.month > 1) {
        this.month = this.month - 1;
      } else {
        this.setMonth({ month: 12 });
        this.setYear({ year: this.year - 1 });
        this.$emit("update");
      }
    },
    nextMonth() {
      if (this.month < 12) {
        this.month = this.month + 1;
      } else {
        this.setMonth({ month: 1 });
        this.setYear({ year: this.year + 1 });
        this.$emit("update");
      }
    },
    update() {
      this.$emit("update");
    },
    async getBudgetData() {
      let previous = await this.$http.get("/paid-media-budgets", {
        params: {
          year: this.previousYear,
          month: this.previousMonth,
          clientId: this.budget.client_id
        }
      });
      let budget = previous.data;

      if (budget) {
        let projected = 0;
        let actual = 0;

        budget.funds.map(fund => {
          projected += fund.projected;
          if (
            fund.type !== "Google Adwords" &&
            fund.type !== "Google Ads"
          ) {
            actual += fund.actual;
          }
        });

        this.prevMonthOverUnder = projected - actual;
      } else {
        this.prevMonthOverUnder = 0;
      }
    },
    copyAmount(value) {
      let input = document.createElement("input");
      input.value = value;
      document.body.appendChild(input);
      input.focus();
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    },
    async destroy(fund) {
      if (window.confirm("Are you sure you want to delete this fund?")) {
        await this.$http.delete(
          "/paid-media-budgets/" + this.budget.id + "/funds/" + fund.id
        );
      }
      this.update();
    }
  }
};
</script>

<style lang="scss">
#fund-manager {
  #month-selector {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: 0 10px 15px;

    a {
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
    }
  }
  .month {
    width: 25%;
    margin-bottom: 20px;
  }

  .fund-hold {
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: .05em;
    font-size: 12px;
    color: #999;
  }
  button.action:disabled {
    background-color: #999;
    border-color: #999;
    opacity: .5;
    cursor: not-allowed;
  }

  .no-funds {
    margin-top: 45px;
    padding: 15px;
    text-align: center;
    background: $light-gray;
    border: 1px solid $medium-gray;
  }

  .table {
    display: table;
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
  }

  .table-row {
    display: table-row;
  }

  .table-cell {
    display: table-cell;
    padding: 5px 10px;
    vertical-align: middle;
    border-bottom: 1px solid $light-gray;
    text-align: right;

    &:first-of-type {
      text-align: left;
      width: 300px;
    }

    .icon {
      display: none;
      color: lighten($dark-gray, 5%);

      &:hover {
        color: $black;
      }

      &:active {
        transform: scale(0.9);
        color: lighten($dark-gray, 5%);
      }
    }

    &:hover {
      .icon {
        display: inline-block;
      }
    }
  }

  .header {
    font-weight: 700;
  }

  .fund {
    color: $black;

    &:hover {
      background: lighten($blue, 50%);
    }

    .fa-info {
      color: $dark-gray;
      padding-right: 10px;

      &:hover {
        color: $black;
      }
    }
  }

  .client-link {
    float: right;
    padding: 5px;
  }

  .investment,
  .remaining {
    table-layout: fixed;

    th {
      background: $medium-blue;
      color: $white;
      padding: 2px;
      text-align: center;
      border: 1px solid $medium-blue;
    }

    .empty {
      visibility: hidden;
    }
    td {
      text-align: center;
      border: 1px solid $medium-gray;
      background: $light-gray;
      font-size: 18px;

      &.highlight-blue {
        font-weight: 600;
        background: #d4eaf6;
      }

      &.highlight-yellow {
        font-weight: 600;
        background: #fcf7df;
      }
    }
  }

  .funds {
    .fund > .table-cell { position: relative; }
    table-layout: fixed;
    margin-top: 45px;
    .fa-edit {
      padding-right: 5px;
      color: $medium-gray;
    }
    .purpose-container {
      padding-right: 5px;
      .panel {
        display: none;
        position: absolute;
        color: $black;
        background: $light-gray;
        top: calc(100% - 6px);
        z-index: 9;
        &:hover { display: block; }
      }
      &:hover .panel { display: block; }
    }
  }

  .remaining {
    margin-top: 45px;

    th {
      background: $light-gray;
      color: $black;
      border-color: $medium-gray;
    }

    td {
      background: $white;

      &.over {
        background: #dbeed3;
      }

      &.under {
        background: #f0d9d9;
      }

      &.match {
        background: #d4eaf6;
      }
    }
  }

  .delete {
    padding-right: 10px;
    color: $dark-gray;

    &:hover {
      color: $red;
    }
  }
}
</style>
